import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Copy, Share2, Check, Download, Loader2 } from "lucide-react";
import { toJpeg } from "html-to-image";
import { toast } from "sonner";
import { coverFor } from "@/lib/defaultEventImages";
import { BrandIcon } from "@/components/BrandIcons";
import ShareCard from "@/components/ShareCard";

/**
 * SharePromptModal — premium native-feeling share sheet.
 *
 * UX rules (matching modern mobile social apps):
 *   • WhatsApp + X/Twitter: deep-link directly (one tap = correct app)
 *   • Instagram + More:     navigator.share() with the event cover image
 *                           pre-attached as a File when supported, so users
 *                           can pick Instagram (or anything else) straight
 *                           from the OS share sheet with the image included.
 *   • Copy link:            clipboard write
 *   • Save image:           download the event cover to the device
 *
 * Image-aware sharing degrades gracefully:
 *   navigator.share({files: [coverFile], …})  →  preferred
 *   navigator.share({title, text, url})       →  fallback (no file)
 *   copy text + toast                         →  ultimate fallback
 */

const enc = encodeURIComponent;

function safeFileName(s) {
  return (s || "event")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "event";
}

// Detect iOS (iPhone / iPad incl. iPadOS reporting as Mac). Used to switch
// Instagram-button behaviour: on iOS we route through a "save image then
// open IG" flow because iOS Safari's WebShare API can't reliably deliver
// the file into Instagram Story (IG's Story background API requires the
// native UIPasteboard, which web pages can't write to).
function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ identifies as Mac but supports touch.
  return /Mac/.test(ua) && typeof document !== "undefined" && "ontouchend" in document;
}

// Auto-download a File to the user's device (Camera Roll on iOS, Downloads
// folder elsewhere). Uses a blob: URL so it never hits the network.
function downloadFile(file) {
  const a = document.createElement("a");
  const objUrl = URL.createObjectURL(file);
  a.href = objUrl;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
}

// Pre-fetch a remote (or local) image URL and return it as a data: URL.
// We use this to avoid the cross-origin canvas-tainting bug that breaks
// html-to-image snapshots on Safari/iOS — when ShareCard's <img src=...>
// points at a cross-origin URL (even with crossOrigin="anonymous"), iOS
// rejects the canvas read and the resulting JPEG shows the category
// fallback colour where the cover should be. Inlining the cover as a data
// URL makes it same-origin from the canvas's perspective. Returns null on
// any failure so the caller falls back to the raw URL (still works on
// Android + desktop).
async function fetchAsDataUrl(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// Wait for every <img> inside the off-screen story-card to finish loading
// before snapshotting, otherwise html-to-image captures a blank cover.
async function waitForImages(node) {
  const imgs = Array.from(node.querySelectorAll("img"));
  await Promise.all(
    imgs.map((img) =>
      img.complete && img.naturalWidth > 0
        ? Promise.resolve()
        : new Promise((res) => {
            img.addEventListener("load", res, { once: true });
            img.addEventListener("error", res, { once: true });
          })
    )
  );
}

// Snapshot the off-screen <ShareCard> into a 1080×1920 JPEG File. Why JPEG
// instead of PNG: html-to-image PNGs are RGBA and frequently 2-4 MB —
// large enough that iOS Safari has been observed to silently truncate the
// `files` payload in `navigator.share`, or to hand Instagram an
// unrenderable blob. JPEG at quality 0.92 produces a ~250-400 KB file that
// every platform (and IG Story in particular) accepts reliably.
async function buildStoryCardFile(node, baseName) {
  if (!node) return null;
  try {
    await waitForImages(node);
    const dataUrl = await toJpeg(node, {
      cacheBust: true,
      pixelRatio: 1,
      width: 1080,
      height: 1920,
      quality: 0.92,
      backgroundColor: "#F4F0E1",
    });
    const r = await fetch(dataUrl);
    const blob = await r.blob();
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("share: story-card render failed", err);
    return null;
  }
}

export default function SharePromptModal({ event, onClose }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(null); // "native" | "save" | null
  const { pathname } = useLocation();
  const cardRef = useRef(null);

  // Pre-rendered branded story-card File (the 1080×1920 JPEG snapshot).
  // We render this EAGERLY when the modal opens so that, by the time the
  // user taps Instagram / More, the file is already in memory and we can
  // call `navigator.share({ files: [...] })` synchronously inside the
  // click handler.
  //
  // This is the difference between sharing-with-image and sharing-as-text-
  // only on iOS Safari: iOS requires `navigator.share` to be invoked from
  // within the same user-gesture stack as the click. Any `await` between
  // the click and `share()` (e.g. waiting for html-to-image) loses the
  // gesture context, and iOS silently strips the `files` payload — Android
  // doesn't have this restriction, which is why the bug was iOS-only.
  const [cardFile, setCardFile] = useState(null);
  const [cardLoading, setCardLoading] = useState(true);
  // Cover pre-loaded as a `data:` URL. Required for iOS Safari to avoid
  // tainted-canvas errors during the html-to-image snapshot (the cover
  // would otherwise render as a solid category-coloured block in the
  // shared image). See fetchAsDataUrl().
  const [coverDataUrl, setCoverDataUrl] = useState(null);

  const url = useMemo(
    () =>
      typeof window !== "undefined"
        ? `${window.location.origin}/events/${event.id}`
        : `/events/${event.id}`,
    [event.id]
  );

  const message = useMemo(() => {
    const where = event.city ? ` in ${event.city}` : "";
    return `${event.title} on inonit${where} — show up, say hi.`;
  }, [event.title, event.city]);

  const cover = useMemo(() => coverFor(event), [event]);
  const baseName = useMemo(
    () => `inonit-${safeFileName(event.title)}`,
    [event.title]
  );

  // Esc to close.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Eager story-card render on mount. Steps:
  //   1. Fetch the event cover and inline it as a data: URL (this dodges
  //      Safari/iOS canvas-tainting). Without this, the snapshot succeeds
  //      but the cover area renders as the category-fallback colour.
  //   2. After the ShareCard re-renders with the inline cover, wait for
  //      its <img>s to settle, snapshot to JPEG, cache the File.
  // Subsequent share/save taps reuse the cached File so navigator.share
  // can fire synchronously from the user gesture (critical for iOS).
  useEffect(() => {
    let cancelled = false;
    setCardLoading(true);
    setCardFile(null);
    setCoverDataUrl(null);
    (async () => {
      const dataUrl = await fetchAsDataUrl(cover);
      if (cancelled) return;
      setCoverDataUrl(dataUrl); // May be null — ShareCard falls back to URL.
      // Yield two frames so React mounts ShareCard with the new src before
      // we snapshot.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const f = await buildStoryCardFile(cardRef.current, baseName);
      if (!cancelled) {
        setCardFile(f);
        setCardLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [baseName, event.id, cover]);

  const canShareWithFiles = (file) =>
    typeof navigator !== "undefined" &&
    !!navigator.canShare &&
    !!file &&
    navigator.canShare({ files: [file] });

  // Triggers the OS-native share sheet. Uses the pre-rendered story-card
  // File so navigator.share fires synchronously from the click — required
  // by iOS Safari to preserve the `files` payload (otherwise Instagram
  // Story opens with text-only and no cover image).
  const shareNative = ({ withFile = true } = {}) => {
    if (busy) return;
    if (withFile && cardLoading) {
      toast.message("Preparing image — try again in a sec.");
      return;
    }

    const file = withFile ? cardFile : null;
    const wantFiles = withFile && canShareWithFiles(file);

    // iOS quirk: when both `text/url` AND `files` are present, the IG
    // share extension on iOS sometimes treats the share as a "link share"
    // and drops the image. Sending file-only when a file IS available
    // produces the most reliable Instagram Story result. Text+URL is
    // still passed when there's no file (e.g. unsupported browsers).
    const filesPayload = { title: event.title, files: [file] };
    const textPayload  = { title: event.title, text: message, url };

    // NOTE: do not `await` anything between here and `navigator.share()`
    // — iOS Safari treats any intermediate await as losing the gesture.
    if (typeof navigator !== "undefined" && navigator.share) {
      setBusy("native");
      navigator
        .share(wantFiles ? filesPayload : textPayload)
        .catch((e) => {
          if (e?.name === "AbortError") return;
          if (wantFiles && /not allowed|notallowed|abort|cannot/i.test(String(e?.message || e?.name))) {
            return navigator.share(textPayload).catch(() => {
              toast.error("Couldn't share.");
            });
          }
          toast.error("Couldn't share.");
        })
        .finally(() => setBusy(null));
      return;
    }

    // No native share at all — copy the link + nudge.
    (async () => {
      try {
        await navigator.clipboard.writeText(`${message} ${url}`);
        toast.success("Link copied. Paste it wherever you like.");
      } catch {
        toast.error("Sharing isn't supported on this browser.");
      }
    })();
  };

  // iOS-specific Instagram flow: web → Instagram Story is broken via Web
  // Share API (IG's Story background needs native UIPasteboard, which
  // browsers can't write). The realistic UX is:
  //   1. Save the branded card to the user's Photos / Files.
  //   2. Open Instagram's Story camera (deep-link if installed, else app
  //      store).
  //   3. User picks the just-saved image from the gallery thumbnail.
  // We do step 1 + 2 + show a toast for step 3.
  const shareToInstagram = () => {
    if (busy) return;
    if (cardLoading) {
      toast.message("Preparing image — try again in a sec.");
      return;
    }
    const file = cardFile;

    if (isIOS() && file) {
      // Save the JPEG synchronously inside the user gesture so iOS Safari
      // honours the download/preview. Then nudge the user with the manual
      // step Instagram requires.
      try {
        downloadFile(file);
      } catch {
        /* ignore — fall through to native share */
      }
      toast.success(
        "Image saved. Open Instagram → Your Story → tap the gallery thumbnail to pick it.",
        { duration: 7000 }
      );
      // Best-effort deep link to IG's story camera. Does nothing if IG
      // isn't installed.
      setTimeout(() => {
        try { window.location.href = "instagram://story-camera"; } catch { /* noop */ }
      }, 400);
      return;
    }

    // Android + desktop: native Web Share with the file works reliably.
    shareNative({ withFile: true });
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Couldn't copy.");
    }
  };

  const onSaveImage = async () => {
    if (busy) return;
    setBusy("save");
    try {
      const file = cardFile || await buildStoryCardFile(cardRef.current, baseName);
      if (!file) {
        toast.error("Couldn't generate the image.");
        return;
      }
      downloadFile(file);
      toast.success("Saved.");
    } catch {
      toast.error("Couldn't save.");
    } finally {
      setBusy(null);
    }
  };

  // Quick-share targets (URL-based deep links).
  const openWhatsApp = () => {
    window.open(
      `https://wa.me/?text=${enc(`${message} ${url}`)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };
  const openTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${enc(message)}&url=${enc(url)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  // Show "View event" link only when not already on the event's detail page.
  const showPreviewLink = pathname !== `/events/${event.id}`;

  return (
    <div
      data-testid="share-prompt-modal"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:px-4 sm:py-8 bg-ink/55 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Off-screen 1080×1920 branded story-card. Rendered far off-canvas
          (left: -10000) so it doesn't visibly affect the modal but is fully
          laid out and snapshottable by html-to-image. */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: -10000,
          top: 0,
          pointerEvents: "none",
          opacity: 0,
        }}
      >
        <ShareCard ref={cardRef} event={event} coverDataUrl={coverDataUrl} />
      </div>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-prompt-title"
        className="w-full sm:max-w-md bg-paper border-[3px] border-ink rounded-t-3xl sm:rounded-3xl shadow-pop-lg overflow-hidden animate-in slide-in-from-bottom-8 sm:zoom-in-95 duration-300"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b-[2.5px] border-ink">
          <span
            id="share-prompt-title"
            className="text-xs uppercase tracking-[0.3em] font-extrabold"
          >
            Share your event
          </span>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="share-prompt-close"
            className="w-9 h-9 rounded-full border-[2.5px] border-ink flex items-center justify-center hover:bg-ink hover:text-paper transition-colors"
          >
            <X size={16} strokeWidth={2.8} />
          </button>
        </div>

        <div className="p-5 sm:p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
          {/* Preview card */}
          <PreviewCard
            event={event}
            cover={cover}
            interactive={showPreviewLink}
            onClick={onClose}
          />

          {/* Quick-share grid */}
          <div
            data-testid="share-prompt-grid"
            className="grid grid-cols-4 gap-2.5 sm:gap-3 mt-6"
          >
            <ShareBtn
              testid="share-prompt-whatsapp"
              label="WhatsApp"
              brand="whatsapp"
              onClick={openWhatsApp}
            />
            <ShareBtn
              testid="share-prompt-instagram"
              label="Instagram"
              brand="instagram"
              onClick={shareToInstagram}
              busy={busy === "native" || cardLoading}
            />
            <ShareBtn
              testid="share-prompt-twitter"
              label="X"
              brand="x"
              onClick={openTwitter}
            />
            <ShareBtn
              testid="share-prompt-native"
              label="More"
              icon={
                busy === "native" || cardLoading ? (
                  <Loader2 size={22} className="animate-spin" />
                ) : (
                  <Share2 size={22} strokeWidth={2.4} />
                )
              }
              onClick={() => shareNative({ withFile: true })}
              busy={busy === "native" || cardLoading}
            />
          </div>

          {/* Copy link pill */}
          <button
            type="button"
            onClick={onCopy}
            data-testid="share-prompt-copy"
            aria-label="Copy event link"
            className="group w-full mt-4 flex items-center justify-between gap-3 border-[2.5px] border-ink rounded-full pl-4 pr-2 py-2 bg-paper hover:bg-ink hover:text-paper transition-colors"
          >
            <span className="truncate text-[13px] font-medium text-left">
              {url}
            </span>
            <span className="shrink-0 w-9 h-9 rounded-full border-[2.5px] border-ink flex items-center justify-center bg-paper text-ink group-hover:bg-lime transition-colors">
              {copied ? <Check size={15} strokeWidth={2.8} /> : <Copy size={15} strokeWidth={2.4} />}
            </span>
          </button>

          {/* Save image — chunky secondary action */}
          <button
            type="button"
            onClick={onSaveImage}
            disabled={busy === "save"}
            data-testid="share-prompt-save-image"
            className="w-full mt-3 inline-flex items-center justify-center gap-2 border-[2.5px] border-ink rounded-full px-4 py-3 bg-paper hover:bg-lime text-ink font-extrabold text-[11px] uppercase tracking-widest transition-colors disabled:opacity-60"
          >
            {busy === "save" ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} strokeWidth={2.6} />
            )}
            Save image
          </button>

          <button
            type="button"
            onClick={onClose}
            data-testid="share-prompt-dismiss"
            className="w-full mt-2 py-3 text-[11px] uppercase tracking-widest text-ink/55 hover:text-ink transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewCard({ event, cover, interactive, onClick }) {
  const Wrapper = interactive ? Link : "div";
  const props = interactive
    ? {
        to: `/events/${event.id}`,
        onClick,
        className:
          "block border-[2.5px] border-ink rounded-2xl overflow-hidden bg-paper hover:-translate-x-px hover:-translate-y-px hover:shadow-pop-sm transition-all",
      }
    : {
        className:
          "block border-[2.5px] border-ink rounded-2xl overflow-hidden bg-paper",
      };
  return (
    <Wrapper {...props} data-testid="share-prompt-preview">
      <div className="aspect-[16/10] bg-ink/10 relative overflow-hidden">
        <img
          src={cover}
          alt={event.title}
          className="w-full h-full object-cover"
          draggable={false}
        />
        {event.category && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-paper border-[2.5px] border-ink rounded-full px-2.5 py-1 text-[10px] uppercase tracking-widest font-extrabold">
            {event.category}
          </span>
        )}
      </div>
      <div className="px-4 py-3.5">
        <div className="font-display text-[1.35rem] leading-tight tracking-tight font-bold line-clamp-2">
          {event.title}
        </div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-ink/55 mt-1.5 font-extrabold">
          {event.city || "TBD"}
          {event.category ? ` · ${event.category}` : ""}
        </div>
      </div>
    </Wrapper>
  );
}

function ShareBtn({ testid, label, brand, icon, onClick, busy }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      disabled={busy}
      aria-label={`Share to ${label}`}
      className="group flex flex-col items-center gap-2 active:translate-y-px transition-transform disabled:opacity-60"
    >
      <span className="w-14 h-14 inline-flex items-center justify-center rounded-full bg-paper border-[2.5px] border-ink overflow-hidden shadow-pop-sm group-hover:bg-lime group-active:shadow-pop-active transition-[background-color,box-shadow]">
        {brand ? <BrandIcon brand={brand} size={26} /> : icon}
      </span>
      <span className="text-[10px] uppercase tracking-[0.12em] font-extrabold leading-none text-ink/75 group-hover:text-ink">
        {label}
      </span>
    </button>
  );
}
